import {
  evaluateAchievement,
  calculateAchievementPoints
} from "@/helpers/achievements.js";

const NUMBER_OF_FEED_ENTRIES = 10;

const achievements = {
  namespaced: true,
  state: {},
  mutations: {},
  actions: {},
  getters: {
    // Returns a list of achievements the user has fully unlocked based on their codes and score
    getMyUnlockedAchievements(state, getters, rootState, rootGetters) {
      let achievementsData = rootGetters["content/getAchievements"];
      let myCodes = rootGetters.getCodes;

      return achievementsData
        .map((a) => evaluateAchievement(a, myCodes))
        .filter((a) => a.isEarned);
    },
    // Returns a list of achievements the user has not unlocked yet
    getMyLockedAchievements(state, getters, rootState, rootGetters) {
      let achievementsData = rootGetters["content/getAchievements"];
      let myCodes = rootGetters.getCodes;

      return achievementsData
        .map((a) => evaluateAchievement(a, myCodes))
        .filter((a) => !a.isEarned);
    },
    // Returns a combined list of earned and locked achievements, sorted appropriately
    getMyAchievementsList(state, getters) {
      let earnedAchievements = getters.getMyUnlockedAchievements;
      let lockedAchievements = getters.getMyLockedAchievements;

      const sortedEarnedAchievements = earnedAchievements.sort((a, b) => {
        // First compare by isEarned
        if (a.isEarned && !b.isEarned) return -1; // a first
        if (!a.isEarned && b.isEarned) return 1; // b first

        // If both have same isEarned, sort earned ones by tsLastUpdated
        if (a.isEarned && b.isEarned) {
          return new Date(b.tsLastUpdated) - new Date(a.tsLastUpdated);
        }

        // If both unearned, keep original order (or change if you want)
        return 0;
      });

      let mappedAchievements = [
        ...sortedEarnedAchievements,
        ...lockedAchievements
      ];

      return mappedAchievements;
    },
    // Calculates the total achievement points the user has earned from all their unlocked codes
    getMyTotalPoints(state, getters, rootState, rootGetters) {
      let myCodes = rootGetters.getCodes;

      // Calculate total points from all codes
      let totalPoints = calculateAchievementPoints(myCodes);

      return totalPoints;
    },
    /**
     * Generates a feed of earned achievements for all users.
     *
     * For each user entry, this getter:
     *  1. Checks that the user belongs to a group; skips users without a group.
     *  2. Iterates through all achievements and evaluates each one for the user using `evaluateAchievement`.
     *  3. Includes the achievement in the feed if it is fully earned.
     *
     * Each feed item contains user information (ID, full name, color, image)
     * along with the evaluated achievement data.
     *
     * Note: This getter only includes achievements that have been earned.
     */
    generateAchievementFeed(state, getters, rootState, rootGetters) {
      // Get all user entries
      let entries = rootGetters["datastore/getEntries"];
      // Get all achievements data
      const achievementsData = rootGetters["content/getAchievements"];

      let feed = [];

      entries.forEach((user) => {
        // Ensure the user belongs to a group; skip if not
        const foundGroup = rootGetters["groups/getGroupByUserId"](user.userId);
        if (!foundGroup || Object.keys(foundGroup).length === 0) {
          return;
        }

        // Evaluate each achievement for the user
        achievementsData.forEach((achievement) => {
          let _achievement = { ...achievement };

          const result = evaluateAchievement(_achievement, user.codes);

          if (result.isEarned) {
            feed.push({
              userId: user.userId,
              fullname: user.fullname,
              color: user.color,
              userImage: user.userImage,
              achievement: result // fully evaluated achievement
            });
          }
        });
      });

      return feed;
    },
    /**
     * Generates the main achievement feed for display.
     *
     * This getter combines two sources of feed items:
     *  1. Earned achievements for users (from `generateAchievementFeed`).
     *  2. Points awarded by instructors (`getAwardedPoints`).
     *
     * Steps performed:
     *  - Retrieves earned achievements using the `generateAchievementFeed` getter.
     *  - Retrieves awarded points entries from the store.
     *  - Merges both into a single feed array.
     *  - Sorts the feed in descending order based on the timestamp:
     *      - Uses `achievement.tsLastUpdated` if available (earned achievements).
     *      - Otherwise uses `tsLastUpdated` from awarded points.
     *  - Limits the feed to the most recent `NUMBER_OF_FEED_ENTRIES` items.
     *
     * Each feed item includes user or entry info, timestamps, and achievement details if applicable.
     */
    getAchievementFeed(state, getters, rootState, rootGetters) {
      // Get points awarded by instructor as well
      let awardedPointEntries = rootGetters["content/getAwardedPoints"];

      // Get the achievement feed from the existing getter
      let feed = getters.generateAchievementFeed;

      // Add awarded points to the feed as well
      awardedPointEntries.forEach((entry) => {
        feed.push(entry);
      });

      // Sort the feed by tsLastUpdated descending
      // sorts by achievement.tsLastUpdated if available, otherwise tsLastUpdated which is points awarded by instructor
      feed.sort((a, b) => {
        const aTime =
          a.achievement?.tsLastUpdated ?? a.tsLastUpdated ?? a.tsLastUpdated;
        const bTime =
          b.achievement?.tsLastUpdated ?? b.tsLastUpdated ?? b.tsLastUpdated;

        return bTime - aTime; // descending
      });

      console.log("Generated Achievement Feed:", feed);

      return feed; //.slice(0, NUMBER_OF_FEED_ENTRIES); // return only the latest 10 entries
    }
  }
};

export default achievements;
